import type { TDateString, TUuid } from '../types/common';
import {
  EQualificationStatus,
  EQualificationValidityType,
  type TFacilityWriteScope,
  type TQualificationCounts,
  type TQualificationEvaluation,
  type TQualificationRecordValidity,
} from '../types/qualification';
import { getIsoDateYear } from './date';

const VALIDITY_TYPES: ReadonlyArray<EQualificationValidityType> =
  Object.values(EQualificationValidityType);

export const isQualificationValidityType = (
  value: unknown,
): value is EQualificationValidityType => VALIDITY_TYPES.some((type) => type === value);

/**
 * One facility outside the scope makes a whole qualification read-only to the planner, and so
 * does having none left once its facilities were deleted.
 * `=== true` narrows the union under the lenient extension tsconfigs, which lack strictNullChecks.
 */
export const isWithinFacilityScope = (
  scope: TFacilityWriteScope,
  facilityIds: ReadonlyArray<TUuid>,
): boolean => scope.unrestricted === true
  || (facilityIds.length > 0 && facilityIds.every((id) => scope.facilityIds.has(id)));

export const overlapsFacilityScope = (
  scope: TFacilityWriteScope,
  facilityIds: ReadonlyArray<TUuid>,
): boolean => scope.unrestricted === true || facilityIds.some((id) => scope.facilityIds.has(id));

export const defaultQualificationValidUntil = (
  validityType: EQualificationValidityType,
  recordYear: number,
): TDateString | null => (
  validityType === EQualificationValidityType.ANNUAL ? `${recordYear}-12-31` : null
);

/** Such a record could never be valid. */
export const isQualificationEndBeforeYear = (
  validUntil: TDateString | null,
  recordYear: number,
): boolean => !!validUntil && validUntil < `${recordYear}-01-01`;

/** The valid-until day itself still counts; ISO date strings compare like the dates. */
export const isQualificationRecordValid = (
  record: TQualificationRecordValidity,
  referenceDate: TDateString,
): boolean => record.record_year <= getIsoDateYear(referenceDate)
  && (!record.valid_until || record.valid_until >= referenceDate);

/** A record of a later year than the reference date's does not exist yet as of that date. */
export const evaluateQualificationRecords = (
  records: ReadonlyArray<TQualificationRecordValidity>,
  referenceDate: TDateString,
): TQualificationEvaluation => {
  const recorded = records.filter((record) => record.record_year <= getIsoDateYear(referenceDate));
  if (!recorded.length) return { status: EQualificationStatus.NOT_RECORDED, validUntil: null };
  const ends = recorded
    .filter((record) => isQualificationRecordValid(record, referenceDate))
    .map((record) => record.valid_until);
  if (!ends.length) return { status: EQualificationStatus.EXPIRED, validUntil: null };
  return {
    status: EQualificationStatus.VALID,
    validUntil: ends.includes(null) ? null : [...ends].sort().reverse()[0] ?? null,
  };
};

/** The record the status rests on: a valid one where there is one, otherwise the latest. */
export const findStatusRecord = <TRecord extends TQualificationRecordValidity>(
  recordsLatestFirst: ReadonlyArray<TRecord>,
  referenceDate: TDateString,
): TRecord | null => {
  const recorded = recordsLatestFirst.filter(
    (record) => record.record_year <= getIsoDateYear(referenceDate),
  );
  return recorded.find((record) => isQualificationRecordValid(record, referenceDate))
    ?? recorded[0]
    ?? null;
};

/** One evaluation per physician the qualification applies to. */
export const countQualificationStatuses = (
  evaluations: ReadonlyArray<Pick<TQualificationEvaluation, 'status'>>,
): TQualificationCounts => ({
  valid: evaluations.filter(({ status }) => status === EQualificationStatus.VALID).length,
  expired: evaluations.filter(({ status }) => status === EQualificationStatus.EXPIRED).length,
  total: evaluations.length,
});
